import {
    IframeMessage,
    IframeMessageType,
    ProvideScriptsUrlMessage,
    RequestScriptsUrlMessage,
    ResizeMessage
} from "@omni/types/components/props/iframe.props";
import { isDefined } from "@omni/utils/helper.utils";
import { cn } from "@omni/utils/styles";
import { useCallback, useEffect, useRef, useState } from "react";

import { getAssetLocation } from "@/utils/asset.utils";
import { attributeToBoolean } from "@/utils/attributes";

type IframeProps = {
    src: string;
    title: string;
    dynamic: undefined | string;
};

const Iframe = ({ src, title, dynamic: dynamicAttribute }: IframeProps) => {
    const dynamic = attributeToBoolean(dynamicAttribute);

    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [height, setHeight] = useState<string | undefined>(undefined);

    const handleResizeMessage = (message: ResizeMessage) =>
        setHeight(`${message.height}px`);

    const handleRequestInfoMessage = (_message: RequestScriptsUrlMessage) => {
        const provideInfoMessage: ProvideScriptsUrlMessage = {
            type: IframeMessageType.ProvideScriptsUrl,
            scriptsUrl: `${getAssetLocation()}/scripts`
        };

        iframeRef.current?.contentWindow?.postMessage(provideInfoMessage, src);
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const handleOnMessage = useCallback(
        (event: MessageEvent<IframeMessage>) => {
            if (
                !isDefined(event.data.type) ||
                !Object.values(IframeMessageType).includes(event.data.type)
            ) {
                return;
            }

            switch (event.data.type) {
                case IframeMessageType.Resize:
                    handleResizeMessage(event.data);
                    break;
                case IframeMessageType.RequestScriptsUrl:
                    handleRequestInfoMessage(event.data);
                    break;
                default:
                    console.info("Unhandled iFrame message:", event.data);
            }
        },
        [setHeight]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        window.addEventListener("message", handleOnMessage);
        () => {
            window.removeEventListener("message", handleOnMessage);
        };
    }, []);

    return (
        <iframe
            className={cn(
                "tw:border-none tw:w-full",
                dynamic ? "tw:h-0 tw:relative" : "tw:h-[130dvw] tw:md:h-[60dvw]"
            )}
            ref={iframeRef}
            src={src}
            title={title}
            allowFullScreen={true}
            style={{
                height: dynamic ? height : undefined
            }}
        ></iframe>
    );
};

export { Iframe };
